import { useEffect, useState } from 'react';
import { obtenerCentros } from '../services/empresasService';
import { obtenerServicios, obtenerMecanicos, obtenerPartesVehiculo } from '../services/mecanicaService';

const ID_EMPRESA = 2;

/**
 * Caché a nivel de MÓDULO (no de componente ni de estado de React).
 * Vive mientras la pestaña del navegador esté abierta, sobrevive a que
 * RecepcionInicial se desmonte/remonte al cambiar de paso.
 *
 * Puede valer:
 *  - null                -> nunca se ha pedido
 *  - Promise<datos>       -> ya se está pidiendo (alguien más lo disparó)
 *  - { centros, ... }     -> ya está resuelto, listo para usar sin red
 */
let catalogosCache = null;

function cargarCatalogos() {
  return Promise.all([
    obtenerCentros(ID_EMPRESA),
    obtenerServicios(ID_EMPRESA),
    obtenerMecanicos(),
    obtenerPartesVehiculo(),
  ]).then(([centrosData, serviciosData, mecanicosData, partesData]) => {
    // Excluir el centro "NO ASIGNADO"
    const centrosFiltrados = centrosData.filter(
      (centro) => centro.id_centrocosto !== 13
    );

    return {
      centros: centrosFiltrados,
      servicios: serviciosData,
      mecanicos: mecanicosData,
      partesVehiculo: partesData,
    };
  });
}

const yaResuelto = (valor) => valor && !(valor instanceof Promise);

export function useCatalogosMecanica() {
  // Si ya hay datos resueltos en caché, arrancamos con ellos de inmediato
  // (sin esto, el primer render mostraría los selectores vacíos un instante
  // aunque los datos ya estuvieran disponibles).
  const [datos, setDatos] = useState(() => (yaResuelto(catalogosCache) ? catalogosCache : null));
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelado = false;

    // Caso 1: ya está resuelto — no hay nada que pedir.
    if (yaResuelto(catalogosCache)) {
      setDatos(catalogosCache);
      return;
    }

    // Caso 2: nadie ha disparado la carga todavía — la disparamos nosotros.
    // Si otro componente llama a este mismo hook casi al mismo tiempo, verá
    // que catalogosCache ya es una Promise y NO volverá a pedir nada — solo
    // se "engancha" a la misma promesa (ver más abajo).
    if (!catalogosCache) {
      catalogosCache = cargarCatalogos().catch((e) => {
        catalogosCache = null; // si falla, permite reintentar en el próximo montaje
        throw e;
      });
    }

    catalogosCache
      .then((resultado) => {
        if (cancelado) return;
        catalogosCache = resultado; // reemplaza la Promise por el valor ya resuelto
        setDatos(resultado);
      })
      .catch((e) => {
        if (!cancelado) setError(e.message);
      });

    return () => { cancelado = true; };
  }, []);

  return {
    centros: datos?.centros ?? [],
    servicios: datos?.servicios ?? [],
    mecanicos: datos?.mecanicos ?? [],
    partesVehiculo: datos?.partesVehiculo ?? [],
    error,
  };
}

/**
 * si en algún momento se necesita forzar una recarga (ej. después de
 * agregar un mecánico nuevo desde otra pantalla y quieres que aparezca sin
 * recargar toda la página), llama a esto antes de volver a montar el formulario.
 */
export function invalidarCatalogosMecanica() {
  catalogosCache = null;
}